import { ApartmentOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type {
  BuildingRow,
  BuildingSaveRequest,
  FloorRow,
  FloorSaveRequest,
  OrgRow,
  SpaceStatus
} from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import type { HqApi } from '@hq/api-sdk';
import { ellipsisCell } from '@hq/ui-kit';

const STATUS_META: Record<SpaceStatus, { text: string; color: string }> = {
  ACTIVE: { text: '启用', color: 'green' },
  INACTIVE: { text: '停用', color: 'default' },
  LOCKED: { text: '锁定', color: 'orange' }
};

interface BuildingFormValues {
  buildingCode: string;
  buildingName: string;
  campusName?: string;
  address?: string;
  floorCount: number;
  undergroundFloors: number;
  isIndoorMapReady: boolean;
  sortNo: number;
  status: SpaceStatus;
  remark?: string;
}

interface FloorFormValues {
  floorCode: string;
  floorName: string;
  floorNo: number;
  height?: number;
  area?: number;
  deliveryRouteHint?: string;
  sortNo: number;
  status: SpaceStatus;
  remark?: string;
}

/**
 * 楼宇与楼层（`org_building` / `org_floor`）：科室与地标的主地址落到具体楼栋楼层，
 * 支撑分楼层配送路线与室内导航；被科室引用的楼宇/楼层不允许删除。
 */
export interface OrgSpacePageProps {
  hq: HqApi;
  /** 平台视角（web-system）：先选租户再选机构；运营端固定本租户。 */
  platform?: boolean;
}

export function OrgSpacePage({ hq, platform = false }: OrgSpacePageProps) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  // 平台视角：先选租户再选机构
  const [tenantId, setTenantId] = useState<string | undefined>();
  const [orgId, setOrgId] = useState('');
  const [current, setCurrent] = useState<BuildingRow | null>(null);
  const [bOpen, setBOpen] = useState(false);
  const [bEditing, setBEditing] = useState<BuildingRow | null>(null);
  const [fOpen, setFOpen] = useState(false);
  const [fEditing, setFEditing] = useState<FloorRow | null>(null);

  const [bForm] = Form.useForm<BuildingFormValues>();
  const [fForm] = Form.useForm<FloorFormValues>();

  const tenants = useQuery({
    queryKey: ['sys', 'tenant', 'options'],
    queryFn: () => hq.tenant.list({}),
    enabled: platform,
    staleTime: 60 * 1000
  });

  const orgs = useQuery({
    queryKey: ['ops', 'org', 'options', tenantId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.org.list(platform ? { tenantId } : undefined),
    staleTime: 60 * 1000
  });

  const buildings = useQuery({
    queryKey: ['ops', 'building', orgId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.orgStructure.building.list({ orgId }),
    enabled: orgId !== ''
  });

  const floors = useQuery({
    queryKey: ['ops', 'floor', current?.id],
    placeholderData: keepPreviousData,
    queryFn: () => hq.orgStructure.floor.list({ buildingId: current?.id, orgId }),
    enabled: !!current
  });

  const saveBuilding = useMutation({
    mutationFn: (body: BuildingSaveRequest) => hq.orgStructure.building.save(body),
    onSuccess: async (result) => {
      message.success(result.updated ? '楼宇已更新' : '楼宇已新增');
      setBOpen(false);
      setBEditing(null);
      bForm.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'building'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const removeBuilding = useMutation({
    mutationFn: (id: string) => hq.orgStructure.building.remove(id),
    onSuccess: async () => {
      message.success('楼宇已删除');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'building'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败，请稍后重试');
    }
  });

  const saveFloor = useMutation({
    mutationFn: (body: FloorSaveRequest) => hq.orgStructure.floor.save(body),
    onSuccess: async (result) => {
      message.success(result.updated ? '楼层已更新' : '楼层已新增');
      setFOpen(false);
      setFEditing(null);
      fForm.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'floor'] });
      await queryClient.invalidateQueries({ queryKey: ['ops', 'building'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const removeFloor = useMutation({
    mutationFn: (id: string) => hq.orgStructure.floor.remove(id),
    onSuccess: async () => {
      message.success('楼层已删除');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'floor'] });
      await queryClient.invalidateQueries({ queryKey: ['ops', 'building'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败，请稍后重试');
    }
  });

  const orgOptions = (orgs.data ?? []).map((row: OrgRow) => ({
    value: row.id,
    label: `${row.orgName}（${row.orgShortName ?? '-'}）`
  }));

  const openBCreate = () => {
    setBEditing(null);
    bForm.setFieldsValue({
      floorCount: 1,
      undergroundFloors: 0,
      isIndoorMapReady: false,
      sortNo: 0,
      status: 'ACTIVE'
    });
    setBOpen(true);
  };

  const openBEdit = (row: BuildingRow) => {
    setBEditing(row);
    bForm.setFieldsValue({
      buildingCode: row.buildingCode,
      buildingName: row.buildingName,
      campusName: row.campusName ?? undefined,
      address: row.address ?? undefined,
      floorCount: row.floorCount,
      undergroundFloors: row.undergroundFloors,
      isIndoorMapReady: row.isIndoorMapReady,
      sortNo: row.sortNo,
      status: row.status,
      remark: row.remark ?? undefined
    });
    setBOpen(true);
  };

  const openFCreate = () => {
    setFEditing(null);
    fForm.setFieldsValue({ floorNo: 1, sortNo: 0, status: 'ACTIVE' });
    setFOpen(true);
  };

  const openFEdit = (row: FloorRow) => {
    setFEditing(row);
    fForm.setFieldsValue({
      floorCode: row.floorCode,
      floorName: row.floorName,
      floorNo: row.floorNo,
      height: row.height ?? undefined,
      area: row.area ?? undefined,
      deliveryRouteHint: row.deliveryRouteHint ?? undefined,
      sortNo: row.sortNo,
      status: row.status,
      remark: row.remark ?? undefined
    });
    setFOpen(true);
  };

  const buildingColumns: ColumnsType<BuildingRow> = [
    {
      title: '楼宇',
      // 单行展示：编码 / 院区收进悬浮提示，避免行高被拉高
      render: (_, row) => (
        <Tooltip placement="topLeft" title={[row.buildingCode, row.campusName].filter(Boolean).join(' · ')}>
          <Space size={6} align="center">
            <span>{row.buildingName}</span>
            {row.isIndoorMapReady ? <Tag color="blue" style={{ marginInlineEnd: 0 }}>室内导航</Tag> : null}
          </Space>
        </Tooltip>
      )
    },
    {
      title: '层数（地上/地下）',
      width: 130,
      render: (_, row) => `${row.floorCount} / ${row.undergroundFloors}`
    },
    { title: '已建楼层', dataIndex: 'floorRows', width: 90, align: 'right' },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value: SpaceStatus) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '操作',
      width: 150,
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => setCurrent(row)}>
            楼层
          </Button>
          <Button type="link" size="small" onClick={() => openBEdit(row)}>
            编辑
          </Button>
          <Popconfirm
            title="删除该楼宇？"
            description={
              row.floorRows > 0 || row.deptCount > 0
                ? `已有 ${row.floorRows} 个楼层、${row.deptCount} 个科室引用，需先处理`
                : '删除后不可恢复'
            }
            okText="删除"
            cancelText="取消"
            onConfirm={() => removeBuilding.mutate(row.id)}
          >
            <Button type="link" size="small" danger loading={removeBuilding.isPending}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const floorColumns: ColumnsType<FloorRow> = [
    {
      title: '楼层',
      // 单行展示：编码 / 序号收进悬浮提示
      render: (_, row) => (
        <Tooltip placement="topLeft" title={`${row.floorCode} · 序号 ${row.floorNo}`}>
          <span>{row.floorName}</span>
        </Tooltip>
      )
    },
    {
      title: '面积/层高',
      width: 130,
      render: (_, row) => (
        <Typography.Text type="secondary">
          {row.area ? `${row.area} ㎡` : '-'} / {row.height ? `${row.height} m` : '-'}
        </Typography.Text>
      )
    },
    {
      title: '配送要点',
      dataIndex: 'deliveryRouteHint',
      ellipsis: { showTitle: false },
      render: ellipsisCell('deliveryRouteHint')
    },
    { title: '科室数', dataIndex: 'deptCount', width: 80, align: 'right' },
    {
      title: '操作',
      width: 120,
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => openFEdit(row)}>
            编辑
          </Button>
          <Popconfirm
            title="删除该楼层？"
            description={row.deptCount > 0 ? `有 ${row.deptCount} 个科室定位在该层，需先调整` : '删除后不可恢复'}
            okText="删除"
            cancelText="取消"
            onConfirm={() => removeFloor.mutate(row.id)}
          >
            <Button type="link" size="small" danger loading={removeFloor.isPending}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          <ApartmentOutlined />
          楼宇与楼层
        </Space>
      }
      extra={
        <Button
          icon={<ReloadOutlined />}
          loading={buildings.isFetching || floors.isFetching}
          onClick={() => {
            void buildings.refetch();
            void floors.refetch();
          }}
        >
          刷新
        </Button>
      }
    >
      <Space style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary">机构</Typography.Text>
        {platform ? (
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
            placeholder="全部租户"
            value={tenantId}
            onChange={(value) => {
              setTenantId(value ?? undefined);
              setOrgId('');
              setCurrent(null);
            }}
            options={(tenants.data ?? []).map((item) => ({
              value: String(item.id),
              label: `${item.tenantName}（${item.tenantCode}）`
            }))}
          />
        ) : null}
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: 360 }}
          placeholder="请选择机构"
          value={orgId || undefined}
          onChange={(value) => {
            setOrgId(value);
            setCurrent(null);
          }}
          options={orgOptions}
        />
        <Button icon={<PlusOutlined />} type="primary" disabled={orgId === ''} onClick={openBCreate}>
          新建楼宇
        </Button>
      </Space>

      {orgId === '' ? (
        <Alert
          type="info"
          showIcon
          message="先选择机构"
          description="楼宇/楼层用于科室与地标的精确地址（分楼层配送、室内导航）；建好后可在「仓库与科室」里给科室选择楼宇与楼层。"
        />
      ) : (
        <Row gutter={16}>
          <Col xs={24} xl={13}>
            <Card size="small" title="楼宇">
              <Table<BuildingRow>
                rowKey="id"
                size="small"
                columns={buildingColumns}
                dataSource={buildings.data ?? []}
                loading={orgId !== '' && buildings.isPending}
                pagination={false}
                rowClassName={(row) => (row.id === current?.id ? 'ant-table-row-selected' : '')}
                onRow={(row) => ({ onClick: () => setCurrent(row), style: { cursor: 'pointer' } })}
                locale={{ emptyText: '暂无楼宇' }}
              />
            </Card>
          </Col>
          <Col xs={24} xl={11}>
            <Card
              size="small"
              title={current ? `楼层 · ${current.buildingName}` : '楼层'}
              extra={
                <Button icon={<PlusOutlined />} size="small" type="primary" disabled={!current} onClick={openFCreate}>
                  新建楼层
                </Button>
              }
            >
              <Table<FloorRow>
                rowKey="id"
                size="small"
                columns={floorColumns}
                dataSource={floors.data ?? []}
                loading={!!current && floors.isPending}
                pagination={false}
                locale={{ emptyText: current ? '该楼宇暂无楼层' : '先在左侧选择一个楼宇' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Modal
        title={bEditing ? '编辑楼宇' : '新建楼宇'}
        open={bOpen}
        onCancel={() => {
          setBOpen(false);
          setBEditing(null);
        }}
        onOk={() => bForm.submit()}
        confirmLoading={saveBuilding.isPending}
        width={640}
        destroyOnHidden
      >
        <Form<BuildingFormValues>
          form={bForm}
          layout="vertical"
          onFinish={(values) =>
            saveBuilding.mutate({
              id: bEditing?.id,
              orgId,
              buildingCode: values.buildingCode,
              buildingName: values.buildingName,
              campusName: values.campusName,
              address: values.address,
              floorCount: values.floorCount,
              undergroundFloors: values.undergroundFloors,
              isIndoorMapReady: values.isIndoorMapReady,
              sortNo: values.sortNo,
              status: values.status,
              remark: values.remark
            })
          }
        >
          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item
              name="buildingCode"
              label="楼宇编码"
              rules={[
                { required: true, message: '请填写楼宇编码' },
                { pattern: /^[0-9A-Za-z_-]{2,32}$/, message: '字母、数字、下划线或短横线，2–32 位' }
              ]}
              style={{ width: 200 }}
            >
              <Input maxLength={32} placeholder="如 B1" />
            </Form.Item>
            <Form.Item name="buildingName" label="楼宇名称" rules={[{ required: true, message: '请填写楼宇名称' }]} style={{ width: 300 }}>
              <Input maxLength={128} placeholder="如 门诊楼" />
            </Form.Item>
            <Form.Item name="campusName" label="院区" style={{ width: 180 }}>
              <Input maxLength={128} placeholder="如 本部" />
            </Form.Item>
          </Space>

          <Form.Item name="address" label="地址">
            <Input maxLength={255} />
          </Form.Item>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item name="floorCount" label="地上层数" style={{ width: 140 }}>
              <InputNumber min={0} max={200} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="undergroundFloors" label="地下层数" style={{ width: 140 }}>
              <InputNumber min={0} max={10} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="sortNo" label="排序" style={{ width: 120 }}>
              <InputNumber min={0} max={9999} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="status" label="状态" style={{ width: 140 }}>
              <Select
                options={[
                  { value: 'ACTIVE', label: '启用' },
                  { value: 'INACTIVE', label: '停用' },
                  { value: 'LOCKED', label: '锁定' }
                ]}
              />
            </Form.Item>
          </Space>

          <Form.Item name="isIndoorMapReady" label="启用室内导航" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={fEditing ? '编辑楼层' : '新建楼层'}
        open={fOpen}
        onCancel={() => {
          setFOpen(false);
          setFEditing(null);
        }}
        onOk={() => fForm.submit()}
        confirmLoading={saveFloor.isPending}
        width={620}
        destroyOnHidden
      >
        <Form<FloorFormValues>
          form={fForm}
          layout="vertical"
          onFinish={(values) =>
            saveFloor.mutate({
              id: fEditing?.id,
              orgId,
              buildingId: current?.id,
              floorCode: values.floorCode,
              floorName: values.floorName,
              floorNo: values.floorNo,
              height: values.height,
              area: values.area,
              deliveryRouteHint: values.deliveryRouteHint,
              sortNo: values.sortNo,
              status: values.status,
              remark: values.remark
            })
          }
        >
          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item
              name="floorCode"
              label="楼层编码"
              rules={[
                { required: true, message: '请填写楼层编码' },
                { pattern: /^[0-9A-Za-z_-]{2,32}$/, message: '字母、数字、下划线或短横线，2–32 位' }
              ]}
              style={{ width: 200 }}
            >
              <Input maxLength={32} placeholder="如 F3" />
            </Form.Item>
            <Form.Item name="floorName" label="楼层名称" rules={[{ required: true, message: '请填写楼层名称' }]} style={{ width: 220 }}>
              <Input maxLength={64} placeholder="如 3F" />
            </Form.Item>
            <Form.Item name="floorNo" label="序号（负数=地下）" style={{ width: 160 }}>
              <InputNumber min={-10} max={200} style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item name="height" label="层高(m)" style={{ width: 130 }}>
              <InputNumber min={0} max={50} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="area" label="面积(㎡)" style={{ width: 150 }}>
              <InputNumber min={0} max={1000000} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="sortNo" label="排序" style={{ width: 110 }}>
              <InputNumber min={0} max={9999} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="status" label="状态" style={{ width: 140 }}>
              <Select
                options={[
                  { value: 'ACTIVE', label: '启用' },
                  { value: 'INACTIVE', label: '停用' },
                  { value: 'LOCKED', label: '锁定' }
                ]}
              />
            </Form.Item>
          </Space>

          <Form.Item name="deliveryRouteHint" label="配送路线要点">
            <Input.TextArea rows={2} maxLength={500} showCount placeholder="如：货梯东侧，出电梯右转至护士站" />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
